"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";

/* Mindtale line drawings: a list of strokes that ink themselves one after
   another the first time the drawing scrolls into view, then stay put.
   Tones map onto a palette so the same scene works on light and dark
   sections. A stroke made only of `h.01` segments renders as round dots. */

export type LineTone = "ink" | "accent" | "warm";
export type Line = { d: string; tone?: LineTone };
export type LinePalette = Record<LineTone, string>;

export const LIGHT: LinePalette = { ink: "#264653", accent: "#2C969B", warm: "#DC7050" };
export const DARK: LinePalette = { ink: "#FFFFFF", accent: "#6FBFC3", warm: "#DC7050" };

export default function LineDrawing({
  viewBox,
  label,
  lines,
  palette = LIGHT,
  strokeWidth = 2.6,
  delay = 0.1,
  stagger = 0.13,
  duration = 0.7,
  className = "",
}: {
  viewBox: string;
  label: string;
  lines: Line[];
  palette?: LinePalette;
  strokeWidth?: number;
  delay?: number;
  stagger?: number;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const still = useReducedMotion() ?? false;

  // Some mobile WebViews never fire the IntersectionObserver; better to lose
  // the timing than to leave an empty box.
  const [forced, setForced] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setForced(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  const play = inView || forced || still;

  return (
    <div ref={ref} className={className}>
      <svg viewBox={viewBox} className="h-auto w-full" role="img" aria-label={label}>
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          {lines.map((l, i) => {
            const isDots = /^(M[\d.\s-]+h\.01\s*)+$/.test(l.d.trim());
            const at = delay + i * stagger;
            return (
              <motion.path
                key={i}
                d={l.d}
                stroke={palette[l.tone ?? "ink"]}
                strokeWidth={isDots ? strokeWidth * 2.4 : strokeWidth}
                initial={still ? false : { pathLength: 0, opacity: 0 }}
                animate={play ? { pathLength: 1, opacity: 1 } : undefined}
                transition={{
                  pathLength: { duration: still ? 0 : duration, delay: still ? 0 : at, ease: [0.45, 0, 0.2, 1] },
                  opacity: { duration: 0.01, delay: still ? 0 : at },
                }}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}
